import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Segmented } from "../../../components/ui";
import { apiError, getWinbackAudience, getWinbackJob, previewWinback, sendWinback } from "../../../services/winback";
import { formatNumber } from "../../../utils/numbers";
import "../payments-ledger/payments.css";
import "./winback.css";
import SegmentCriteria, { criteriaOf, EMPTY_CRITERIA } from "../notifications/SegmentCriteria";
import "../notifications/notifications.css";
import MailQuotaNotice from "../emails/MailQuotaNotice";

const DAY_OPTIONS = [30, 60, 90, 180, 365];

/** Progress of a group sending, refreshed every 2 s until it ends. */
export const WinbackProgress = ({ job: initial, onDone }) => {
  const { t } = useTranslation();
  const [job, setJob] = useState(initial);

  useEffect(() => {
    if (!job || job.finishedAt) return undefined;
    const timer = setInterval(() => {
      getWinbackJob().then((next) => {
        if (!next) return;
        setJob(next);
        if (next.finishedAt) onDone?.(next);
      });
    }, 2000);
    return () => clearInterval(timer);
  }, [job, onDone]);

  if (!job) return null;
  const done = job.sent + job.skipped + job.failed;
  const percent = job.total ? Math.min(100, Math.round((done / job.total) * 100)) : 100;
  return (
    <div className="winback-progress" role="status" aria-live="polite">
      <div className="winback-progress-head">
        <strong>{job.finishedAt ? t("Group sending finished") : t("Group sending in progress…")}</strong>
        <span>{done} / {job.total}</span>
      </div>
      <div className="winback-progress-bar" aria-hidden="true">
        <span style={{ width: `${percent}%` }} />
      </div>
      <p className="ui-hint">
        {t("winbackCounts", { sent: job.sent, skipped: job.skipped, failed: job.failed })}
        {job.lastError ? ` · ${job.lastError}` : ""}
      </p>
    </div>
  );
};

/**
 * "We've missed you" email: to one user (`user`) or to every inactive user of a segment
 * (no `user`). Letter signed by Alain Foka with the latest videos, or a written message;
 * the preview shows the exact email.
 */
const WinbackComposer = ({ user, onClose, onSent, initialMode = "generic" }) => {
  const { t } = useTranslation();
  const group = !user;
  const [segment, setSegment] = useState(initialMode === "donation" ? "all" : "both");
  // Groupe personnalisé : critères communs aux notifications (formule, activité, donateurs, quiz…)
  const [criteria, setCriteria] = useState(EMPTY_CRITERIA);
  const [days, setDays] = useState(30);
  const [skipRecent, setSkipRecent] = useState(true);
  // Nombre maximum de personnes (vide : tout le segment)
  const [limit, setLimit] = useState("");
  const [audience, setAudience] = useState(null);
  const [mode, setMode] = useState(["generic", "donation", "custom"].includes(initialMode) ? initialMode : "generic");
  const [lang, setLang] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [signed, setSigned] = useState(true);
  // Message rédigé : bouton vers la page de don au lieu du site
  const [donateButton, setDonateButton] = useState(false);
  const [force, setForce] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [job, setJob] = useState(null);

  // Appel au don : texte au hasard (un des trois pour chaque destinataire) ou imposé
  const [variant, setVariant] = useState("");
  const message_ = { mode, lang: lang || undefined, subject, message, signed, cta: mode === "custom" && donateButton ? "don" : "site", ...(mode === "donation" && variant ? { variant: Number(variant) } : {}) };
  // Lettre d'Alain Foka et appel au don : prêts tout de suite ; message rédigé : objet et texte requis
  const ready = mode === "generic" || mode === "donation" || (subject.trim() && message.trim());

  // Number of recipients of the segment
  useEffect(() => {
    if (!group) return;
    setAudience(null);
    const custom = segment === "custom" ? { criteria: criteriaOf(criteria) } : {};
    getWinbackAudience({ segment, days, skipRecent, ...custom }).then(setAudience);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group, segment, days, skipRecent, JSON.stringify(criteria)]);

  // Preview refreshed while typing (the user, or an example for a group)
  useEffect(() => {
    if (!ready) {
      setPreview(null);
      return undefined;
    }
    const timer = setTimeout(() => {
      previewWinback({ ...message_, userId: user?._id })
        .then((email) => {
          setPreview(email);
          setPreviewError("");
        })
        .catch((error) => {
          setPreview(null);
          setPreviewError(apiError(error) || t("Preview unavailable"));
        });
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, lang, subject, message, signed, donateButton, variant, user?._id]);

  const send = async () => {
    setSending(true);
    try {
      if (group) {
        const started = await sendWinback({
          ...message_,
          segment,
          days,
          skipRecent,
          ...(segment === "custom" ? { criteria: criteriaOf(criteria) } : {}),
          ...(maxRecipients ? { limit: maxRecipients } : {}),
        });
        setJob(started.job);
        setConfirming(false);
        toast.info(t("winbackStarted", { count: started.job.total }));
      } else {
        const { result } = await sendWinback({ ...message_, force, userIds: [user._id] });
        if (result.sent) toast.success(t("Email sent"));
        if (result.skipped?.length) toast.info(result.skipped[0].reason);
        if (result.failed?.length) toast.error(result.failed[0].reason);
        onSent?.(result);
      }
    } catch (error) {
      toast.error(apiError(error) || t("Error updating data"));
    } finally {
      setSending(false);
    }
  };

  const matching = audience?.count;
  const maxRecipients = parseInt(limit, 10) > 0 ? parseInt(limit, 10) : null;
  // Destinataires réels : le segment, ou moins si un nombre maximum est fixé
  const count = matching === undefined ? undefined : maxRecipients ? Math.min(maxRecipients, matching) : matching;
  const name = user ? [user.name, user.last_name].filter(Boolean).join(" ") || user.email : "";

  return (
    <Modal show={true} onHide={onClose} centered size="xl" dialogClassName="ui-modal">
      <Modal.Header>
        <Modal.Title>
          {mode === "donation"
            ? group ? t("Call for donations") : t("Send a call for donations to {{name}}", { name })
            : group ? t("Win back inactive users") : t("Send « We've missed you » to {{name}}", { name })}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {job ? (
          <WinbackProgress job={job} onDone={(done) => onSent?.(done)} />
        ) : (
          <div className="ledger-composer">
            <div className="ui-form ledger-composer-form">
              {group && (
                <>
                  <div className="ui-field ui-field-full">
                    <span className="ui-label">{t("Recipients")}</span>
                    <Segmented
                      label={t("Recipients")}
                      value={segment}
                      onChange={setSegment}
                      options={[
                        { value: "both", label: t("Inactive and never seen") },
                        { value: "inactive", label: t("Inactive users") },
                        { value: "never", label: t("Never seen users") },
                        { value: "all", label: t("All users") },
                        { value: "custom", label: t("Custom group") },
                      ]}
                    />
                    <p className="ui-hint">
                      {segment === "custom"
                        ? t("Choose the plan, the activity, donors or quiz players, the language and the countries.")
                        : segment === "all"
                        ? t("Every user who can receive emails, active or not.")
                        : segment === "never"
                        ? t("Registered but never seen in the app.")
                        : segment === "inactive"
                        ? t("Used the app before, but not recently.")
                        : t("Inactive users, and registered users never seen in the app.")}
                    </p>
                  </div>
                  {segment === "custom" && (
                    <div className="ui-field-full">
                      <SegmentCriteria idPrefix="winback" value={criteria} onChange={(partial) => setCriteria((current) => ({ ...current, ...partial }))} />
                    </div>
                  )}
                  {segment !== "all" && segment !== "custom" && (
                  <div className="ui-field">
                    <label htmlFor="winback-days">{t("Without activity for")}</label>
                    <select id="winback-days" className="ui-input" value={days} onChange={(e) => setDays(Number(e.target.value))}>
                      {DAY_OPTIONS.map((value) => (
                        <option key={value} value={value}>{t("{{days}} days or more", { days: value })}</option>
                      ))}
                    </select>
                  </div>
                  )}
                  <label className="ledger-check">
                    <input type="checkbox" checked={skipRecent} onChange={(e) => setSkipRecent(e.target.checked)} />
                    {t("Skip people already contacted in the last 30 days")}
                  </label>
                  <div className="ui-field">
                    <label htmlFor="winback-limit">{t("Maximum number of people")}</label>
                    <input
                      id="winback-limit"
                      className="ui-input"
                      type="number"
                      inputMode="numeric"
                      min={1}
                      step={1}
                      placeholder={t("Everyone")}
                      value={limit}
                      onChange={(e) => setLimit(e.target.value.replace(/[^\d]/g, ""))}
                    />
                  </div>
                  <p className="ui-hint ui-field-full winback-limit-hint">
                    {t("With a maximum, the most recently active people are contacted first, then those who registered most recently. Run it again later to contact the next ones.")}
                  </p>
                  <div className="winback-audience ui-field-full" aria-live="polite">
                    <i className="ri-group-line" aria-hidden="true"></i>
                    {count === undefined ? t("Counting…") : t("winbackRecipients", { count, formatted: formatNumber(count) })}
                    {maxRecipients && matching !== undefined && matching > count && (
                      <span className="winback-of">{t("winbackOutOf", { formatted: formatNumber(matching) })}</span>
                    )}
                    <span className="ui-hint">{t("Disabled or banned accounts, invalid addresses and unsubscribed people are never contacted.")}</span>
                  </div>
                </>
              )}

              <div className="ui-field ui-field-full">
                <Segmented
                  label={t("Message type")}
                  value={mode}
                  onChange={(value) => {
                    setMode(value);
                    // Appel au don : par défaut à tous les utilisateurs (modifiable)
                    if (value === "donation" && group) setSegment("all");
                  }}
                  options={[
                    { value: "generic", label: t("Letter from Alain Foka") },
                    { value: "donation", label: t("Call for donations") },
                    { value: "custom", label: t("Written message") },
                  ]}
                />
                <p className="ui-hint">
                  {mode === "donation"
                    ? t("Letter signed by Alain Foka inviting to support AFO MEDIA, with a « Make a donation » button to the donation page (card or Mobile Money), in the user's language.")
                    : mode === "generic"
                    ? t("« We've missed you »: new content is waiting, with the latest videos, and a more stable and friendlier platform. Signed by Alain Foka, in the user's language.")
                    : t("Your own subject and message. {prenom} is replaced by the first name. Leave an empty line between paragraphs.")}
                </p>
              </div>
              {mode === "donation" && (
                <div className="ui-field ui-field-full">
                  <Segmented
                    label={t("Text")}
                    value={variant}
                    onChange={setVariant}
                    options={[
                      { value: "", label: t("Random") },
                      { value: "1", label: t("Text {{n}}", { n: 1 }) },
                      { value: "2", label: t("Text {{n}}", { n: 2 }) },
                      { value: "3", label: t("Text {{n}}", { n: 3 }) },
                    ]}
                  />
                  <p className="ui-hint">{t("Random: each recipient receives one of the three approved texts, drawn at random. Choose a text to see it in the preview or to send only that one.")}</p>
                </div>
              )}
              {mode === "custom" && (
                <>
                  <div className="ui-field ui-field-full">
                    <label htmlFor="winback-subject">{t("Subject")}</label>
                    <input id="winback-subject" className="ui-input" value={subject} maxLength={150} onChange={(e) => setSubject(e.target.value)} />
                  </div>
                  <div className="ui-field ui-field-full">
                    <label htmlFor="winback-message">{t("Message")}</label>
                    <textarea id="winback-message" className="ui-input ledger-textarea" rows={9} maxLength={5000} value={message} onChange={(e) => setMessage(e.target.value)} />
                  </div>
                  <label className="ledger-check">
                    <input type="checkbox" checked={signed} onChange={(e) => setSigned(e.target.checked)} />
                    {t("Sign as Alain Foka")}
                  </label>
                  <label className="ledger-check">
                    <input type="checkbox" checked={donateButton} onChange={(e) => setDonateButton(e.target.checked)} />
                    {t("« Make a donation » button to the donation page")}
                  </label>
                </>
              )}
              <div className="ui-field">
                <label htmlFor="winback-lang">{t("Language")}</label>
                <select id="winback-lang" className="ui-input" value={lang} onChange={(e) => setLang(e.target.value)}>
                  <option value="">{t("User's language")}</option>
                  <option value="fr">Français</option>
                  <option value="en">English</option>
                </select>
              </div>
              {!group && (
                <label className="ledger-check">
                  <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} />
                  {t("Send even if already contacted in the last 30 days")}
                </label>
              )}
            </div>

            <div className="ledger-preview">
              <span className="ui-label">
                {t("Preview")}
                {preview?.to ? ` · ${t("to")} ${preview.to}` : group ? ` · ${t("example")}` : ""}
                {preview?.variant ? ` · ${t("text {{n}}", { n: preview.variant })}` : ""}
              </span>
              {preview ? (
                <>
                  <p className="ledger-preview-subject">{preview.subject}</p>
                  <iframe title={t("Preview")} className="ledger-preview-frame" srcDoc={preview.html} sandbox="" />
                </>
              ) : (
                <p className="ui-hint">{previewError || (ready ? t("Loading…") : t("Write the subject and the message to see the email."))}</p>
              )}
            </div>
          </div>
        )}
      </Modal.Body>
      {!job && (
        <div className="mail-quota-slot">
          <MailQuotaNotice recipients={group ? count || 0 : 1} />
        </div>
      )}
      <Modal.Footer>
        {job ? (
          <button type="button" className="ui-btn" onClick={onClose}>
            {job.finishedAt ? t("Close") : t("Close (sending continues)")}
          </button>
        ) : confirming ? (
          <>
            <span className="winback-confirm">{t("winbackConfirm", { count: count || 0, formatted: formatNumber(count || 0) })}</span>
            <button type="button" className="ui-btn ui-btn-secondary" onClick={() => setConfirming(false)}>
              {t("Cancel")}
            </button>
            <button type="button" className="ui-btn" disabled={sending} onClick={send}>
              <i className="ri-mail-send-line" aria-hidden="true"></i>
              {sending ? t("Sending…") : t("Confirm sending")}
            </button>
          </>
        ) : (
          <>
            <button type="button" className="ui-btn ui-btn-secondary" onClick={onClose}>
              {t("Cancel")}
            </button>
            <button
              type="button"
              className="ui-btn"
              disabled={!ready || sending || Boolean(previewError) || (group && !count)}
              onClick={group ? () => setConfirming(true) : send}
            >
              <i className="ri-mail-send-line" aria-hidden="true"></i>
              {sending ? t("Sending…") : group ? t("winbackSendTo", { count: count || 0, formatted: formatNumber(count || 0) }) : t("Send")}
            </button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default WinbackComposer;
